export interface AquaTree { revisions: Record<string, unknown>; file_index: Record<string, string> }
export interface FieldDefinition { label: string; name: string; field_type: string; required: boolean; options?: string[] }
export interface TemplateLite { id: string; title: string; subtitle?: string; builtin: boolean; fields: FieldDefinition[] }

export interface AquafyResponse { success: boolean; aqua_tree: AquaTree; file_name: string; file_hash: string; file_size: number }
export interface RequiredFile { content_hash: string; file_name: string; present: boolean }
export interface RequiredTree { hash: string; present: boolean }
export interface InspectResponse { success: boolean; satisfied: boolean; required_files: RequiredFile[]; required_trees: RequiredTree[] }
export interface PerFile { file_name: string; content_hash: string; matched: boolean }
export interface VerifyResponse {
  success: boolean; is_valid: boolean; status: string;
  logs: unknown[]; wasm_outputs: Record<string, unknown>;
  claim_checks: Record<string, unknown>; per_file: PerFile[]
}

export interface VerifyFile { file_name: string; bytes_base64: string }
export interface VerifyRequest {
  kind?: 'tree' | 'selective'; main: unknown;
  linked_trees?: unknown[]; files?: VerifyFile[]; check_claims?: boolean
}
export interface InspectRequest { trees: unknown[]; have_file_hashes?: string[] }

export class AquaApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'AquaApiError'
  }
}

export class AquaApi {
  constructor(private base: string) {
    this.base = base.replace(/\/$/, '')
  }

  private async json<T>(path: string, init: RequestInit): Promise<T> {
    let resp: Response
    try {
      resp = await fetch(this.base + path, init)
    } catch (e) {
      throw new AquaApiError(0, `Network error: ${(e as Error).message}`)
    }
    let body: any = null
    try { body = await resp.json() } catch { /* non-json error */ }
    if (!resp.ok || (body && body.success === false)) {
      const msg = (body && body.error) || `Request failed (${resp.status})`
      throw new AquaApiError(resp.status, msg)
    }
    return body as T
  }

  aquafyFile(file: File): Promise<AquafyResponse> {
    const fd = new FormData()
    fd.append('file', file, file.name)
    return this.json('/api/widget/aquafy', { method: 'POST', body: fd })
  }

  listTemplates(): Promise<{ success: boolean; templates: TemplateLite[] }> {
    return this.json('/api/widget/templates', { method: 'GET' })
  }

  getTemplate(id: string): Promise<{ success: boolean; template: TemplateLite }> {
    return this.json(`/api/widget/templates/${encodeURIComponent(id)}`, { method: 'GET' })
  }

  aquafyForm(template_id: string, payloads: Record<string, unknown>): Promise<{ success: boolean; aqua_tree: AquaTree }> {
    return this.json('/api/widget/aquafy-form', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ template_id, payloads }),
    })
  }

  redact(aqua_tree: unknown, revision_hash: string, disclosed_paths: string[]): Promise<{ success: boolean; selective_tree: unknown }> {
    return this.json('/api/widget/redact', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ aqua_tree, revision_hash, disclosed_paths }),
    })
  }

  inspect(req: InspectRequest): Promise<InspectResponse> {
    return this.json('/api/widget/inspect', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
    })
  }

  verify(req: VerifyRequest): Promise<VerifyResponse> {
    return this.json('/api/widget/verify', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(req),
    })
  }
}

/** Base64-encode an ArrayBuffer (browser-safe, chunked to avoid stack limits). */
export function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let bin = ''
  const chunk = 0x8000
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk))
  }
  return btoa(bin)
}
